import { useCallback, useEffect, useState, type JSX } from 'react';
import { api, type BankQuestion } from '../../api.ts';
import { QuestionForm, TYPE_LABELS } from '../../components/QuestionForm.tsx';
import { Markup } from '../../components/Markup.tsx';

/**
 * The bank of questions, independent of any quiz.
 *
 * A question lives here once and is placed into as many quizzes as needed, each with its own
 * weight. Editing is deliberately careful: once somebody has answered a question its options are
 * frozen and the server offers a copy instead, because stored answers point at option ids and
 * rewriting them would quietly change what past learners are recorded as having chosen.
 */

export function QuestionBankPage(): JSX.Element {
  const [questions, setQuestions] = useState<BankQuestion[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const load = useCallback((term: string): void => {
    api.authoring
      .questions(term || undefined)
      .then(setQuestions)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить банк'),
      );
  }, []);

  useEffect(() => load(''), [load]);

  return (
    <>
      <div className="page-head">
        <h1>Банк вопросов</h1>
        <p className="muted">
          Один вопрос можно поставить в несколько тестов — у каждого будет свой вес.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <QuestionForm
        submitLabel="Добавить в банк"
        onSubmit={async (draft) => {
          await api.authoring.createQuestion(draft);
          load(search);
        }}
      />

      <div className="row" style={{ marginBottom: 18 }}>
        <input
          className="input input--inline"
          placeholder="Поиск по тексту вопроса"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') load(search);
          }}
        />
        <button type="button" className="btn btn--ghost btn--small" onClick={() => load(search)}>
          Найти
        </button>
      </div>

      {questions === null && !error ? <p className="muted">Загружаем…</p> : null}
      {questions?.length === 0 ? <div className="notice">Ничего не нашлось.</div> : null}

      {(questions ?? []).map((question) => (
        <section key={question.id} className="editcard">
          <header className="editcard__head">
            <span className="badge">{TYPE_LABELS[question.type]}</span>
            <span className="rowcard__main" />
            <span className="muted" style={{ fontSize: '0.82rem' }}>
              в тестах: {question.used_in_quizzes}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() =>
                void api.authoring
                  .duplicateQuestion(question.id)
                  .then(() => load(search))
                  .catch((cause: unknown) =>
                    setError(cause instanceof Error ? cause.message : 'Не удалось скопировать'),
                  )
              }
            >
              Копия
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              onClick={() => {
                if (window.confirm('Убрать вопрос из банка? Пройденные тесты не изменятся.')) {
                  void api.authoring
                    .archiveQuestion(question.id, true)
                    .then(() => load(search))
                    .catch((cause: unknown) =>
                      setError(cause instanceof Error ? cause.message : 'Не удалось убрать'),
                    );
                }
              }}
            >
              В архив
            </button>
          </header>

          <Markup text={question.text_md} />

          {question.options.length > 0 ? (
            <ul className="plainlist">
              {question.options.map((option) => (
                <li key={option.id}>
                  <span style={{ color: option.is_correct ? 'var(--done)' : 'var(--text-faint)' }}>
                    {question.type === 'ordering'
                      ? `${option.position}.`
                      : option.is_correct
                        ? '✓'
                        : '·'}
                  </span>{' '}
                  {option.label}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </>
  );
}
